import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";

import type { SimpleProjectDto, SystemGroupDto } from "@/api/types";
import { MassAudienceSelector } from "@/components/MassAudienceSelector/MassAudienceSelector";
import { useSurveyQueries } from "@/queries/surveys/queryOptions";
import { useTranslation } from "@/translations";

import type { CreateMassSurveyFormValues } from "../CreateMassSurveyPage";

type CreateMassSurveyFormAudienceSelectorProps = {
  selectedGroup: SystemGroupDto | undefined;
  selectedProjects: SimpleProjectDto[];
  isError: boolean;
  onConfirmSelection: ({ group, projects }: { group: SystemGroupDto; projects: SimpleProjectDto[] }) => void;
};

export function CreateMassSurveyFormAudienceSelector({
  selectedGroup,
  selectedProjects,
  isError,
  onConfirmSelection,
}: CreateMassSurveyFormAudienceSelectorProps): React.ReactNode {
  const { t } = useTranslation();
  const surveyQueries = useSurveyQueries();
  const [currentGroup, setCurrentGroup] = useState<SystemGroupDto | undefined>(selectedGroup);
  const audienceGroupsQuery = useQuery(surveyQueries.massSurveyGroups());
  const audienceProjectsQuery = useQuery(surveyQueries.massSurveyProjects(currentGroup?.id));

  return (
    <MassAudienceSelector<CreateMassSurveyFormValues>
      selectedGroup={selectedGroup}
      selectedProjects={selectedProjects}
      isError={isError}
      isDisabled={false}
      audienceGroupsQuery={audienceGroupsQuery}
      audienceProjectsQuery={audienceProjectsQuery}
      subtitle={t("page.mass-surveys.create.audience-selector.drawer.subtitle")}
      description={({ projects }) =>
        t("page.mass-surveys.create.audience-selector.drawer.description", { amount: projects })
      }
      formErrorWrapperName="projects"
      onConfirmSelection={onConfirmSelection}
      onInternalGroupChange={setCurrentGroup}
    />
  );
}
